import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Button } from '~/components/ui/button';
import { Textarea } from '~/components/ui/input';
import { Badge } from '~/components/ui/badge';
import { api } from '~/lib/api';
import type { PracticeDetail, PracticeSessionT } from '@resonance/shared-types';

const AUTOSAVE_DEBOUNCE_MS = 1000;

export const Route = createFileRoute('/_authed/practices/$id')({
  component: PracticeDetailPage,
});

function PracticeDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const { data: practice, refetch } = useQuery<PracticeDetail>({
    queryKey: ['practice', id],
    queryFn: () => api.getPractice(id),
    refetchOnMount: 'always',
  });
  const [response, setResponse] = useState('');
  const [autosaveStatus, setAutosaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedRef = useRef<string>('');

  const respond = useMutation({
    mutationFn: (input: { text?: string; skipped?: boolean }) => api.respondPractice(id, input),
    onSuccess: () => {
      setResponse('');
      lastSavedRef.current = '';
      setAutosaveStatus('idle');
      qc.invalidateQueries({ queryKey: ['practice', id] });
      qc.invalidateQueries({ queryKey: ['practices'] });
    },
  });

  // Reset textarea when current step changes — and seed from any prior draft.
  useEffect(() => {
    const current = practice?.sessions.find((s) => s.stepKey === practice?.currentStep);
    const initial = current?.userText ?? '';
    setResponse(initial);
    lastSavedRef.current = initial;
    setAutosaveStatus('idle');
  }, [practice?.currentStep, practice?.id]);

  // Debounced draft autosave. Forgiveness + narrative get this; compassion's
  // 1-2 line notes don't need it.
  const autosaveEnabled = practice && practice.kind !== 'compassion' && practice.status === 'active';
  useEffect(() => {
    if (!autosaveEnabled) return;
    const trimmed = response.trim();
    if (trimmed === lastSavedRef.current.trim()) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    setAutosaveStatus('saving');
    autosaveTimer.current = setTimeout(async () => {
      try {
        await api.respondPractice(id, { text: trimmed, draft: true });
        lastSavedRef.current = trimmed;
        setAutosaveStatus('saved');
      } catch {
        setAutosaveStatus('error');
      }
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [response, autosaveEnabled, id]);

  if (!practice) {
    return <section className="mx-auto max-w-2xl px-4 py-12">loading…</section>;
  }

  const current = practice.sessions.find((s) => s.stepKey === practice.currentStep) ?? null;
  const completedSessions = practice.sessions.filter((s) => s.completedAt !== null);

  // Compassion is the only kind that has skip + a small "done" affordance.
  const isCompassion = practice.kind === 'compassion';
  const isCompleted = practice.status === 'completed';

  const kindLabel = practice.kind.charAt(0).toUpperCase() + practice.kind.slice(1);

  return (
    <section className="mx-auto max-w-2xl px-4 sm:px-6 py-10 sm:py-14 practice-root animate-rise-soft">
      <div className="flex items-baseline justify-between gap-3 print:hidden">
        <div>
          <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
            {kindLabel}
            {!isCompleted && (
              <> · step {completedSessions.length + 1} of {practice.totalSteps}</>
            )}
          </div>
          {practice.subject && (
            <p className="font-serif text-sm text-muted-foreground italic mt-1">
              {practice.subject}
            </p>
          )}
        </div>
        <Link to="/practices" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
          ← practices
        </Link>
      </div>

      {!isCompleted && (
        <div className="mt-3 h-1 rounded-full bg-muted overflow-hidden print:hidden">
          <div
            className="h-full bg-foreground transition-all duration-500"
            style={{
              width: `${(completedSessions.length / Math.max(1, practice.totalSteps)) * 100}%`,
            }}
          />
        </div>
      )}

      {isCompleted && practice.output && (
        <article className="mt-10 whitespace-pre-wrap font-serif text-[17px] leading-[1.7] text-foreground">
          {practice.output}
        </article>
      )}

      {!isCompleted && current && (
        <div className="mt-10 animate-rise">
          <h2 className="font-serif text-[26px] sm:text-3xl leading-[1.15]">
            {current.title}
          </h2>
          <p className="mt-4 whitespace-pre-wrap leading-[1.65] text-[15.5px] text-muted-foreground">
            {current.prompt}
          </p>

          <Textarea
            value={response}
            onChange={(e) => setResponse(e.target.value)}
            rows={isCompassion ? 3 : 10}
            placeholder={isCompassion ? 'optional note · or skip' : '…'}
            className={`mt-6 ${isCompassion ? '' : 'font-serif text-[17px] leading-[1.7]'}`}
            disabled={respond.isPending}
          />

          <div className="mt-4 flex items-center gap-2">
            <Button
              disabled={respond.isPending || (!response.trim() && !isCompassion)}
              onClick={() => respond.mutate({ text: response.trim() })}
              size="lg"
            >
              {respond.isPending
                ? 'saving…'
                : isCompassion
                  ? 'Done'
                  : 'Save & continue'}
            </Button>
            {isCompassion && (
              <Button
                variant="ghost"
                size="lg"
                disabled={respond.isPending}
                onClick={() => respond.mutate({ skipped: true })}
              >
                Skip today
              </Button>
            )}
            {!isCompassion && (
              <span
                className="ml-auto text-[12px] text-muted-foreground tabular-nums"
                aria-live="polite"
              >
                {autosaveStatus === 'saving' && 'autosaving…'}
                {autosaveStatus === 'saved' && '✓ draft saved'}
                {autosaveStatus === 'error' && '⚠ autosave failed'}
              </span>
            )}
          </div>

          {respond.data?.assistantText && (
            <div className="mt-8 pl-4 border-l-2 border-[var(--color-accent)] animate-rise">
              <div className="text-[11px] uppercase tracking-[0.12em] text-[var(--color-accent)]/80 mb-1.5">
                reflection
              </div>
              <p className="whitespace-pre-wrap leading-relaxed text-[15px] text-muted-foreground">
                {respond.data.assistantText}
              </p>
            </div>
          )}
        </div>
      )}

      {completedSessions.length > 0 && !isCompleted && (
        <details className="mt-14 print:hidden">
          <summary className="text-[13px] text-muted-foreground cursor-pointer hover:text-foreground">
            past steps ({completedSessions.length})
          </summary>
          <ul className="mt-4 space-y-4">
            {completedSessions.map((s) => (
              <CompletedSessionRow key={s.stepKey} practiceId={practice.id} session={s} />
            ))}
          </ul>
        </details>
      )}

      {isCompleted && (
        <div className="mt-10">
          <Badge variant="success">complete</Badge>
        </div>
      )}

      {isCompleted && completedSessions.length > 0 && (
        <details className="mt-12 print:hidden">
          <summary className="text-[13px] text-muted-foreground cursor-pointer hover:text-foreground">
            your responses ({completedSessions.length}) — edit or reset
          </summary>
          {practice.kind === 'forgiveness' && (
            <p className="mt-3 text-[12px] text-muted-foreground italic">
              edits won't auto-regenerate the compiled letter (that would re-run the AI).
              reset a step to re-do it and re-compile.
            </p>
          )}
          <ul className="mt-4 space-y-4">
            {completedSessions.map((s) => (
              <CompletedSessionRow key={s.stepKey} practiceId={practice.id} session={s} />
            ))}
          </ul>
        </details>
      )}

      {isCompleted && (
        <div className="mt-10 flex items-center gap-3 border-t border-border pt-6 print:hidden">
          <a href={api.exportPracticeUrl(practice.id)} download>
            <Button size="sm" variant="secondary">
              download markdown
            </Button>
          </a>
          <Button size="sm" variant="ghost" onClick={() => window.print()}>
            print or save as PDF
          </Button>
        </div>
      )}
    </section>
  );
}

function CompletedSessionRow({
  practiceId,
  session,
}: {
  practiceId: string;
  session: PracticeSessionT;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [draft, setDraft] = useState(session.userText ?? '');

  useEffect(() => {
    setDraft(session.userText ?? '');
  }, [session.userText]);

  const edit = useMutation({
    mutationFn: (text: string) => api.editPracticeSession(practiceId, session.stepKey, text),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['practice', practiceId] });
      qc.invalidateQueries({ queryKey: ['practices'] });
    },
  });

  const reset = useMutation({
    mutationFn: () => api.resetPracticeSession(practiceId, session.stepKey),
    onSuccess: () => {
      setConfirmReset(false);
      qc.invalidateQueries({ queryKey: ['practice', practiceId] });
      qc.invalidateQueries({ queryKey: ['practices'] });
    },
  });

  const trimmed = draft.trim();
  const dirty = trimmed.length > 0 && trimmed !== (session.userText ?? '').trim();

  return (
    <li className="rounded-[var(--radius)] border border-border bg-background px-4 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
          {session.title}
        </div>
        {!editing && !confirmReset && (
          <div className="flex items-center gap-3 text-[12px]">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              edit
            </button>
            <button
              type="button"
              onClick={() => setConfirmReset(true)}
              className="text-muted-foreground hover:text-destructive transition-colors"
            >
              reset
            </button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="mt-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={6}
            className="text-[14px] leading-relaxed"
            disabled={edit.isPending}
            autoFocus
          />
          <div className="mt-2 flex items-center gap-2">
            <Button
              size="sm"
              disabled={!dirty || edit.isPending}
              onClick={() => edit.mutate(trimmed)}
            >
              {edit.isPending ? 'saving…' : 'save'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={edit.isPending}
              onClick={() => {
                setDraft(session.userText ?? '');
                setEditing(false);
              }}
            >
              cancel
            </Button>
            {edit.isError && (
              <span className="text-[12px] text-destructive">save failed</span>
            )}
          </div>
        </div>
      ) : (
        <p className="mt-2 whitespace-pre-wrap leading-relaxed text-[14px]">
          {session.userText}
        </p>
      )}

      {!editing && session.assistantText && (
        <p className="mt-2 text-muted-foreground italic whitespace-pre-wrap leading-relaxed text-[13.5px]">
          {session.assistantText}
        </p>
      )}

      {confirmReset && (
        <div className="mt-3 flex items-center gap-2 rounded-[var(--radius-sm)] border border-destructive/30 bg-destructive/5 px-3 py-2 text-[13px]">
          <span className="text-foreground">reset this step? your input and reflection will be cleared.</span>
          <Button
            size="sm"
            variant="destructive"
            disabled={reset.isPending}
            onClick={() => reset.mutate()}
          >
            {reset.isPending ? 'resetting…' : 'yes, reset'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={reset.isPending}
            onClick={() => setConfirmReset(false)}
          >
            cancel
          </Button>
        </div>
      )}
    </li>
  );
}
